/**
 * The import promise ribbon's entrance (§27b): each part of the line arrives
 * just after the one before it, and then the bar never moves again.
 *
 * Its own module for the reason `revealMotion` and `sheenMotion` are — a file
 * exporting a component may not also export constants without breaking fast
 * refresh.
 *
 * **Why a one-pass entrance and not a rotator.** The obvious thing to do with
 * a persistent bar is cycle two or three messages through it. Three reasons
 * not to: Kabex has one dominant message, and the research that favours
 * rotation is about shops with several concurrent promotions; rotation is
 * auto-updating content under WCAG 2.2.2, which — unlike moving content — has
 * no five-second grace and would oblige us to ship a pause control; and a
 * rotator is a timer and a repaint running for as long as the tab is open, on
 * every page of the site. This costs four transforms, once.
 *
 * **Why pure CSS and not `motion`.** The bar is in the prerendered HTML and
 * is the first thing on the page. A JavaScript entrance would either hold it
 * blank until the runtime arrives on an idle network, or pop it in late. CSS
 * keyframes run as soon as the stylesheet does, which for this site is in the
 * same HTML document — and `animation-fill-mode: both` is what makes the
 * staggered start safe, since each part holds its `from` state only for its
 * own delay rather than being hidden by a rule that JavaScript must undo.
 *
 * The entrance does not repeat on client-side navigation: the ribbon lives in
 * `SiteHeader`, inside the persistent layout route, so it mounts once per
 * hard load and survives every route change after it.
 */

/** Registered by `ImportPromiseRibbonMessage`; the parts reference it by name. */
export const RIBBON_ENTRANCE = 'kbxRibbonIn';

/**
 * Up and in. Six pixels, not twenty: this is a 40px bar, and a rise taller
 * than a third of it reads as the text falling into place rather than
 * settling. `translate3d` keeps the part on the compositor.
 */
export const ribbonEntranceKeyframes = {
  from: { opacity: 0, transform: 'translate3d(0, 6px, 0)' },
  to: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
} as const;

export const RIBBON_DURATION_MS = 420;

/**
 * The gap between one part and the next. Long enough to read as a sequence
 * rather than as one block wobbling, short enough that the whole line is
 * settled inside a second — a visitor should never be waiting for the bar.
 */
export const RIBBON_STEP_MS = 110;

/** Decelerating: the part arrives quickly and eases into place. */
export const RIBBON_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

/** Parts in order: promise, proof, action, arrow. */
export const RIBBON_PART_COUNT = 4;

/** When the last part has finished, and therefore when the sheen may start. */
export const RIBBON_SETTLED_MS = (RIBBON_PART_COUNT - 1) * RIBBON_STEP_MS + RIBBON_DURATION_MS;

/* ------------------------------------------------------------------ wave */

/**
 * The travelling wave: a ripple that runs through the headline, character by
 * character, and then leaves the bar alone until the next pass.
 *
 * The entrance above is a better-behaved animation — it costs four transforms
 * and never runs again — but it only ever plays in the first second of a hard
 * load, which in practice means almost nobody sees it. This is the version
 * that is actually noticed, and it is priced accordingly: every character is
 * its own element with its own infinite animation.
 *
 * Three things keep that price down.
 *
 * 1. **Only the headline waves.** The proof clause, the action and the arrow
 *    stay still. Roughly forty elements, not ninety, and the one line the
 *    visitor is meant to read is the one that moves.
 * 2. **Each character moves for a sixth of the loop.** A character lifts and
 *    settles inside 14% of the cycle and is parked at its resting transform
 *    for the rest. The *wave* is continuous — see `ribbonWaveCycleMs` — but
 *    at any instant only about six of the forty characters are in motion.
 * 3. **Transform and opacity only, and no `will-change`.** Declaring
 *    `will-change` on forty spans would promote forty permanent layers,
 *    which is the one thing that would make this expensive; without it the
 *    browser promotes them only while the ripple is actually passing.
 *
 * WCAG 2.2.2 covers moving content that starts automatically and runs past
 * five seconds, and asks for a mechanism to pause, stop *or hide* it. The
 * ribbon's dismiss control is that mechanism, and it is permanent: closing
 * the bar ends the motion for the rest of the session. Reduced-motion
 * visitors never start it.
 */
export const RIBBON_WAVE = 'kbxRibbonWave';

/**
 * One character's lift.
 *
 * Three pixels on a 40px bar, and back down inside 840ms. Far enough to be
 * plainly moving, short enough that the line never looks like it is coming
 * apart — the text stays on its baseline for five of every six seconds. The
 * small opacity dip travels with it so the ripple reads as light moving
 * across the words rather than as the letters bouncing.
 */
export const ribbonWaveKeyframes = {
  '0%': { transform: 'translate3d(0, 0, 0)', opacity: 1 },
  '7%': { transform: 'translate3d(0, -3px, 0)', opacity: 0.72 },
  '14%': { transform: 'translate3d(0, 0, 0)', opacity: 1 },
  '100%': { transform: 'translate3d(0, 0, 0)', opacity: 1 },
} as const;

/**
 * The lag from one character to the next, which is what turns forty
 * independent lifts into a single wave travelling left to right. At 30ms a
 * forty-character headline takes 1.2s to ripple through — readable as one
 * motion rather than as a row of letters twitching.
 *
 * This is the dial to turn if the bar ever feels busy: raising it slows the
 * wave and lengthens the loop with it, because the cycle is derived from it.
 */
export const RIBBON_WAVE_STEP_MS = 30;

/**
 * How long one character's loop lasts, for a line of `count` characters.
 *
 * Tied to the length rather than fixed, and that is what makes the ripple
 * continuous. Each character is delayed by `RIBBON_WAVE_STEP_MS` more than
 * the one before it, so the wave takes `count × step` to cross the line; set
 * the loop to exactly that and the first character begins its next lift as
 * the last finishes its current one. There is always a ripple somewhere and
 * never a gap.
 *
 * An earlier version fixed this at six seconds, which left four of every six
 * seconds with nothing moving at all — long enough that the bar read as
 * static unless you happened to glance at the right moment.
 *
 * The floor stops a very short string from flickering: ten characters at
 * 30ms would otherwise loop three times a second.
 */
export const ribbonWaveCycleMs = (count: number) => Math.max(count * RIBBON_WAVE_STEP_MS, 900);

/* --------------------------------------------------------------- socials */

/**
 * The social icons arriving in the ribbon, one after another.
 *
 * From md up these icons appear at the moment the visitor scrolls far enough
 * for the utility strip to fold away — so the entrance is tied to something
 * the visitor just did, at the moment they are looking at the top of the
 * page. It re-runs every time they come back, because the icons unmount when
 * the strip reopens.
 *
 * Below md there is no strip to fold, so the row is simply always there and
 * this fires once on load, where — like the headline's own entrance — hardly
 * anyone is watching. That is what `RIBBON_SOCIAL_PULSE` is for: the arrival
 * is only half the attention these icons were asked to attract, and the other
 * half has to happen after the page has stopped settling.
 */
export const RIBBON_SOCIAL = 'kbxRibbonSocialIn';

/**
 * Drop in and overshoot.
 *
 * The icons come from above because that is where the strip they used to
 * live in was, so they read as having moved rather than appeared. The 1.18
 * peak is the "pop" — it is what makes the eye go there — and it settles back
 * to 1 so the resting bar has no trace of it.
 */
export const ribbonSocialKeyframes = {
  '0%': { opacity: 0, transform: 'translate3d(0, -8px, 0) scale(0.5)' },
  '55%': { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1.18)' },
  '100%': { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
} as const;

export const RIBBON_SOCIAL_DURATION_MS = 460;

/** The lag between one icon and the next, so five icons read as a sequence. */
export const RIBBON_SOCIAL_STEP_MS = 80;

/** Slight overshoot, matching the scale peak in the keyframes. */
export const RIBBON_SOCIAL_EASING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

/**
 * When the last icon of a row of `count` has finished arriving.
 *
 * The pulse below has to start after this, not during it: both animations
 * drive `transform` on the same element, and two animations on one property
 * do not blend — the later-declared one wins outright for as long as it is
 * running. Overlapping them would not soften the entrance, it would delete it.
 */
export const ribbonSocialSettledMs = (count: number) =>
  Math.max(count - 1, 0) * RIBBON_SOCIAL_STEP_MS + RIBBON_SOCIAL_DURATION_MS;

/* -------------------------------------------------- socials: idle pulse */

/**
 * The icons' idle pulse: a ripple that crosses the row three times after it
 * has settled, and then stops for good.
 *
 * **Why finite.** The note above argues against looping these icons beside
 * the headline wave, and that argument holds for a *permanent* loop: two
 * timers running for the whole session on every page is where a brand line
 * turns into a carousel. But the entrance alone only ever fires on arrival,
 * and a visitor who reaches the ribbon a beat later sees nothing move. Three
 * passes is the compromise — enough to be caught by someone who was not
 * watching the exact moment the row appeared, and self-terminating, so the
 * resting bar is as still as it was before.
 *
 * It also keeps us out of WCAG 2.2.2 entirely rather than leaning on the
 * dismiss control as the wave does: the whole sequence is over inside ten
 * seconds of the row arriving, so there is no auto-updating content to
 * provide a pause mechanism for.
 *
 * **Why `fill-mode: none`, which is the one subtle part.** This animation is
 * declared *after* the entrance on the same element, so it wins any property
 * they share while it is running. With `both` it would hold its own `from`
 * state from the first frame — overriding the entrance for the whole delay
 * and leaving the icons parked at full scale instead of dropping in. With
 * `none` it contributes nothing before its delay and nothing after its last
 * pass, so the entrance owns the arrival and the element falls back to its
 * resting style afterwards. Which is exactly the rest state anyway, since
 * both keyframe tracks end where the element naturally sits.
 *
 * **What it costs.** Five elements, `transform` only, no `will-change`, and
 * each one is in motion for 18% of its cycle — so about one icon is moving at
 * any instant, for three cycles, and then nothing is. Transform-only means
 * the compositor handles it without a layout or a paint.
 */
export const RIBBON_SOCIAL_PULSE = 'kbxRibbonSocialPulse';

/**
 * Two beats, like a heartbeat, then a long rest.
 *
 * A single lift at this size reads as a glitch — 16px of glyph moving three
 * pixels once is easy to mistake for a rendering hiccup. Two beats, the
 * second smaller, reads as deliberate. `translate3d` before `scale` in every
 * keyframe so the interpolation never has to reconcile two different
 * transform-function orders, which browsers fall back to matrix blending for.
 *
 * Opacity is deliberately absent. The resting dim lives on the button and
 * this runs on a wrapper inside it, so an opacity keyframe here would
 * multiply with that one and read as a dip rather than a brighten.
 */
export const ribbonSocialPulseKeyframes = {
  '0%': { transform: 'translate3d(0, 0, 0) scale(1)' },
  '4%': { transform: 'translate3d(0, -3px, 0) scale(1.16)' },
  '9%': { transform: 'translate3d(0, 0, 0) scale(1)' },
  '13%': { transform: 'translate3d(0, -2px, 0) scale(1.1)' },
  '18%': { transform: 'translate3d(0, 0, 0) scale(1)' },
  '100%': { transform: 'translate3d(0, 0, 0) scale(1)' },
} as const;

/**
 * One full cycle: ~470ms of motion and ~2.1s of stillness.
 *
 * The rest is the point. A pulse every half second is a nag; one every two
 * and a half seconds is a glance invitation, and it is what keeps the row
 * from competing with the headline wave running beside it.
 */
export const RIBBON_SOCIAL_PULSE_DURATION_MS = 2600;

/** The lag from one icon to the next, which makes the row ripple rather than flash. */
export const RIBBON_SOCIAL_PULSE_STEP_MS = 140;

/** Three, then still. See above for why this is a count and not `infinite`. */
export const RIBBON_SOCIAL_PULSE_PASSES = 3;

/**
 * The beat between the row settling and the first pulse.
 *
 * Long enough that the entrance reads as finished — a pulse treading on the
 * overshoot it just made would look like the icons failed to land.
 */
export const RIBBON_SOCIAL_PULSE_HOLD_MS = 1100;

/** When icon `index` of a row of `count` should begin its first pass. */
export const ribbonSocialPulseDelayMs = (index: number, count: number) =>
  ribbonSocialSettledMs(count) + RIBBON_SOCIAL_PULSE_HOLD_MS + index * RIBBON_SOCIAL_PULSE_STEP_MS;

/* ---------------------------------------------------------------- phones */

/**
 * The phone numbers arriving in the ribbon's left column.
 *
 * Same trigger as the social icons opposite them — the utility strip folding
 * away on scroll — and deliberately the same direction of travel: both halves
 * of that strip rise into the ribbon, so a visitor sees one row hand its
 * contents to another rather than two unrelated things appear.
 *
 * What it does *not* share is the overshoot. `ribbonSocialKeyframes` peaks at
 * `scale(1.18)` because a 16px glyph needs the pop to be noticed at all; the
 * same peak on a 13px phone number is text that bounces, and bouncing text at
 * the top of a page reads as a layout bug rather than a flourish. So the
 * numbers slide and fade on the message's own easing and never scale.
 */
export const RIBBON_PHONE = 'kbxRibbonPhoneIn';

export const ribbonPhoneKeyframes = {
  '0%': { opacity: 0, transform: 'translate3d(0, -6px, 0)' },
  '100%': { opacity: 1, transform: 'translate3d(0, 0, 0)' },
} as const;

/**
 * The lag between the icon and the number, and between the two numbers.
 *
 * Wider than the icons' 80ms despite there being fewer parts: three items
 * stepped at 80ms is over almost before it starts, and the row opposite is
 * stepping five. 120ms lets the two sides of the bar settle together.
 */
export const RIBBON_PHONE_STEP_MS = 120;
